// ============================================================================
// expenseCategories — taxonomia de categorias de SAÍDA + classificador por título
// ============================================================================
// Categorias criadas a partir dos títulos reais das contas (jul/2026). O
// classificador `inferSaidaCategory(titulo)` deduz a categoria pelo título — usado
// pra auto-categorizar contas novas e pra recategorizar em massa.
//
// 07/10/2026: o dropdown do Contas a Pagar passou a usar o plano de contas
// OFICIAL do servidor (fin_categorias, agrupado pela linha da DRE — ver
// utils/categoriasAjuda.js). SAIDA_CATEGORIES_GROUPED virou a LISTA DE
// RESERVA (quando a leitura falha, com aviso) e a lista do caixa da recepção
// (que não lê o plano de contas). SAIDA_CATEGORY_LABELS cobre também os
// códigos novos (só rótulo).
//
// 06/10/2026 (Financeiro Fase 0): o classificador só decide quando o título é
// INEQUÍVOCO. Os curingas que jogavam qualquer coisa na folha ('emprestimo',
// 'adiantamento', 'pagamento', 'diferenca') e 'agua' → copa saíram; o que não
// é reconhecido cai em `a_classificar` (e não mais em `outros_saida`), pra
// ficar visível como pendência de classificação em vez de sumir em "Outros".
// ============================================================================

// value → label. Ordem por grupo (usada no dropdown agrupado abaixo).
export const SAIDA_CATEGORY_LABELS = {
  // Pessoas
  folha_salarios: 'Salários e Folha',
  comissoes: 'Comissões',
  beneficios: 'Benefícios (VT/VR/Ajuda)',
  // Ocupação
  aluguel: 'Aluguel',
  energia: 'Energia (Luz)',
  telefone_internet: 'Telefone e Internet',
  // Operacional
  produtos: 'Produtos e Insumos',
  limpeza: 'Limpeza',
  copa_cafe: 'Copa e Café',
  uniformes: 'Uniformes',
  manutencao: 'Manutenção e Reformas',
  compras_gerais: 'Compras e Materiais',
  // Administrativo
  // 06/10/2026 (revisão da DRE): o imposto sobre vendas DA EMPRESA e a taxa
  // de cartão têm categoria própria — com ela, a DRE troca a estimativa
  // (6% / 3% da receita) pelo valor real em vez de somar os dois.
  impostos_vendas: 'Imposto sobre vendas da empresa (DAS/Simples/ISS)',
  mdr_antecipacao: 'Taxa de cartão (MDR/antecipação)',
  impostos_encargos: 'Impostos e Encargos',
  contabilidade: 'Contabilidade',
  juridico: 'Jurídico e Cartório',
  sistemas_software: 'Sistemas e Software',
  marketing: 'Marketing e Tráfego',
  treinamento_educacao: 'Treinamento e Educação',
  // Outros
  reembolsos: 'Reembolsos',
  investimentos: 'Investimentos',
  aquisicao_equipamentos: 'Aquisição de Equipamentos',
  reformas: 'Reformas',
  transferencia_saida: 'Transferência Saída',
  sangria: 'Sangria',
  outros_saida: 'Outros',
  a_classificar: 'A classificar', // fallback do classificador (06/10/2026)
  // Legado (valores antigos — só pra rotular linhas não recategorizadas)
  despesas_financeiras: 'Despesas Financeiras',
  outras_despesas: 'Outras Despesas',
  despesas_marketing: 'Despesas de Marketing',
  despesas_administrativas: 'Despesas Administrativas',
  pagamento_fornecedor: 'Pagamento Fornecedor',
  comissao: 'Comissão',
  aluguel_legacy: 'Aluguel',
  despesa: 'Despesa (genérica)',
  // 07/10/2026: códigos do plano de contas oficial (fin_categorias) que o
  // dropdown novo do Contas a Pagar grava (lista do servidor, /api/fin
  // ?op=categorias). Só RÓTULO — o dropdown de reserva abaixo não muda. Com
  // isso o Fluxo de Caixa e as listas mostram o nome em vez do código.
  produtos_uso: 'Produtos de uso e insumos',
  produtos_revenda: 'Produtos para revenda',
  lavanderia: 'Lavanderia',
  condominio: 'Condomínio',
  iptu: 'IPTU',
  agua: 'Água',
  seguro: 'Seguro',
  alarme: 'Alarme e monitoramento',
  valet: 'Valet / manobrista',
  encargos_folha: 'Encargos da folha (INSS/FGTS)',
  decimo_terceiro_ferias: '13º salário e férias (pagos)',
  provisao_13_ferias: 'Provisão de 13º e férias',
  taxas_licencas: 'Taxas e licenças (TFE, prefeitura)',
  tarifas_bancarias: 'Tarifas bancárias e PIX',
  juros_encargos: 'Juros e encargos de dívida',
  emprestimo_principal: 'Empréstimo (principal)',
  parcelamento_tributario: 'Parcelamento tributário (principal)',
  irpj_csll: 'IRPJ/CSLL',
  mutuo_grupo: 'Mútuo entre empresas do grupo',
  prolabore_pago: 'Pró-labore pago',
  retirada_socio: 'Retirada de sócio',
  distribuicao_lucros: 'Distribuição de lucros',
  adiantamento_socio: 'Adiantamento a sócio',
  suprimento: 'Suprimento',
  adiantamento_funcionario: 'Adiantamento a funcionário',
  emprestimo_funcionario: 'Empréstimo a funcionário',
  // 20/10/2026 (financeiro-013, vales): o vale de quem NÃO está na folha (o Avec desconta). Só RÓTULO.
  vale_comissionado: 'Vale de comissionado',
};

export const SAIDA_CATEGORIES_GROUPED = [
  { group: 'Pessoas', items: ['folha_salarios', 'comissoes', 'beneficios'] },
  { group: 'Ocupação', items: ['aluguel', 'energia', 'telefone_internet'] },
  { group: 'Operacional', items: ['produtos', 'limpeza', 'copa_cafe', 'uniformes', 'manutencao', 'compras_gerais'] },
  { group: 'Administrativo', items: ['impostos_vendas', 'mdr_antecipacao', 'impostos_encargos', 'contabilidade', 'juridico', 'sistemas_software', 'marketing', 'treinamento_educacao'] },
  { group: 'Outros', items: ['a_classificar', 'reembolsos', 'investimentos', 'aquisicao_equipamentos', 'transferencia_saida', 'sangria', 'outros_saida'] },
].map(g => ({ group: g.group, items: g.items.map(v => ({ value: v, label: SAIDA_CATEGORY_LABELS[v] || v })) }));

export function saidaCategoryLabel(value) {
  return SAIDA_CATEGORY_LABELS[value] || value || 'Sem categoria';
}

function norm(s) {
  return String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

// Código de quem o classificador não reconhece (aparece no grupo "Outros").
export const SAIDA_A_CLASSIFICAR = 'a_classificar';

// Deduz a categoria de uma saída pelo título. Regras em ordem de prioridade —
// as mais específicas primeiro. Espelha o classificador validado em jul/2026,
// sem os curingas (06/10/2026). Não reconheceu → 'a_classificar'.
export function inferSaidaCategory(titulo) {
  const n = norm(titulo);
  if (!n.trim()) return SAIDA_A_CLASSIFICAR;
  const has = (...ws) => ws.some(w => n.includes(w));
  const word = (w) => new RegExp(`\\b${w}\\b`).test(n);

  if (has('reembolso')) return 'reembolsos';
  if (has('marketing', 'growth', 'assessoria digital', 'trafego', 'anuncio', 'grafica', 'agencia', 'impulsion', 'social media')) return 'marketing';
  if (has('cartorio', 'contrato', 'homologacao', 'custas', 'juridico')) return 'juridico';
  if (has('aluguel') || word('vaga')) return 'aluguel';
  if (has('darf', 'tributo', 'inss', 'fgts', 'simples nacional', 'imposto', 'prefeitura') || word('das') || word('tfe') || word('cef')) return 'impostos_encargos';
  if (has('contabil', 'honorario')) return 'contabilidade';
  if (has('avec', 'software')) return 'sistemas_software';
  if (has('vivo', 'internet', 'telefone') || word('claro') || word('tim')) return 'telefone_internet';
  if (has('limpeza', 'diarista', 'faxina')) return 'limpeza';
  // 'agua' saiu (06/10/2026): conta de água (Sabesp) não é copa.
  if (has('cafe', 'xicara', 'cerveja', 'bebida')) return 'copa_cafe';
  if (has('manutencao', 'conserto', 'concerto', 'telhado', 'cadeira', 'reforma', 'capas')) return 'manutencao';
  if (has('interlux', 'uniforme', 'roupa')) return 'uniformes';
  if (has('educacao', 'jb ed', 'treinamento', 'curso')) return 'treinamento_educacao';
  // 'emprestimo' e 'adiantamento' saíram (06/10/2026): empréstimo é dívida e
  // adiantamento pode ser de fornecedor — nenhum dos dois é folha por si só.
  if (has('folha', 'salario', 'rescisao', 'horas extra', 'jovem aprendiz', 'jovem valor', 'nube', 'estagio', 'dias trab')) return 'folha_salarios';
  if (has('ajuda de custo', 'vale transporte', 'vale refeicao', 'bonus') || word('vt') || word('vr')) return 'beneficios';
  if (has('comissao')) return 'comissoes';
  if (word('luz') || has('energia', 'enel', 'cpfl', 'eletropaulo', 'cemig')) return 'energia';
  if (has('produto', 'material', 'viking', 'pro beleza', 'botox', 'insumo')) return 'produtos';
  if (has('compra', 'mercado livre')) return 'compras_gerais';
  // O curinga ('pagamento', 'diferenca') → folha saiu (06/10/2026): "pagamento
  // fornecedor X" ou "diferença boleto" não são folha.
  return SAIDA_A_CLASSIFICAR;
}
